import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Input, Card, Button, Space, Empty, Popconfirm, Grid, Dropdown, Tooltip, Checkbox, Modal, message } from "antd";
import {
    SearchOutlined,
    DeleteOutlined,
    PlusOutlined,
    FileTextOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    UserOutlined,
    GlobalOutlined,
    ApartmentOutlined,
    ArrowRightOutlined,
    SortAscendingOutlined,
    FilterOutlined,
    CheckOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    LockOutlined,
    TagOutlined,
    MessageOutlined,
    CheckSquareOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/fr";
import "./workspace.css";
import { useDispatch, useSelector } from "react-redux";
import { RequestDeleteAnalyse, RequestFetchAnalysesSummary } from "../../Actions/review.action";
import {
    closeCommercialGateModal,
    fetchCommercialUsage,
    openCommercialGateModal,
} from "../../Actions/commercialControl.action";
import PasswordChangeModal from "../utilisateurs/PasswordChangeModal";
import { useTranslation } from "react-i18next";
import {
    buildLockedCommercialDecision,
    canStartCompleteAnalysisFromUsage,
    getUsageByTypeFromSnapshot,
} from "../../utils/commercialCapabilities";
import CommercialGateModal from "../stage-review/commercial/CommercialGateModal";

// ── helpers ─────────────────────────────────────────────────────────────────

const normalizeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    try {
        const p = JSON.parse(val);
        return Array.isArray(p) ? p.filter(Boolean) : [val];
    } catch {
        return String(val)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }
};

const SORT_OPTIONS = [
    { key: "recent", labelKey: "dashboard.sort.recent", defaultLabel: "Récents" },
    { key: "oldest", labelKey: "dashboard.sort.oldest", defaultLabel: "Anciens" },
    { key: "title_asc", labelKey: "dashboard.sort.title_asc", defaultLabel: "Titre A→Z" },
];

const WorkspaceSkeleton = ({ viewMode = "grid", count = 6, tWorkspace }) => {
    const items = Array.from({ length: count }, (_, index) => index);
    const metaPatterns = [
        [0, 1, 2],
        [0, 1],
        [],
        [0],
        [0, 2],
        [1, 2],
    ];
    const titlePatterns = ["long", "medium", "short", "medium", "short", "medium"];
    const footerCommentPatterns = [true, true, false, true, false, false];

    if (viewMode === "list") {
        return (
            <div className="workspace-skeleton workspace-skeleton-list" aria-hidden="true">
                {items.map((item) => (
                    <div key={item} className="workspace-skeleton-list-card">
                        <div className="workspace-skeleton-list-leading">
                            <div className="workspace-skeleton-list-icon skeleton-shimmer" />
                            <div className="workspace-skeleton-list-pill skeleton-shimmer" />
                        </div>
                        <div className="workspace-skeleton-list-body">
                            <div className="workspace-skeleton-list-title-row">
                                <div className="workspace-skeleton-line workspace-skeleton-list-title skeleton-shimmer" />
                                <div className="workspace-skeleton-list-pill workspace-skeleton-list-pill-secondary skeleton-shimmer" />
                            </div>
                            <div className="workspace-skeleton-line workspace-skeleton-subtitle skeleton-shimmer" />
                            <div className="workspace-skeleton-inline-meta">
                                <div className="workspace-skeleton-inline-meta-line workspace-skeleton-inline-meta-short skeleton-shimmer" />
                                <div className="workspace-skeleton-inline-meta-line workspace-skeleton-inline-meta-medium skeleton-shimmer" />
                            </div>
                        </div>
                        <div className="workspace-skeleton-list-side">
                            <div className="workspace-skeleton-comment-pill skeleton-shimmer" />
                            <div className="workspace-skeleton-time skeleton-shimmer" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="workspace-skeleton" aria-hidden="true">
            <div className="workspace-skeleton-heading">
                <span className="workspace-skeleton-kicker">
                    {tWorkspace("dashboard.loading.kicker", { defaultValue: "Workspace" })}
                </span>
                <p className="workspace-skeleton-copy">
                    {tWorkspace("dashboard.loading.copy", {
                        defaultValue: "Loading your latest analyses, metadata, and review activity…",
                    })}
                </p>
            </div>
            <Row gutter={[16, 16]}>
                {items.map((item) => (
                    <Col xs={24} sm={12} lg={8} xl={6} xxl={6} key={item}>
                        <div className="workspace-skeleton-card">
                            <div className="workspace-skeleton-card-top">
                                <div className="workspace-skeleton-top-left">
                                    <div className="workspace-skeleton-icon skeleton-shimmer" />
                                    <div className="workspace-skeleton-pill skeleton-shimmer" />
                                    {item % 3 !== 0 && (
                                        <div className="workspace-skeleton-pill workspace-skeleton-pill-soft skeleton-shimmer" />
                                    )}
                                </div>
                            </div>

                            <div className="workspace-skeleton-title-block">
                                <div
                                    className={`workspace-skeleton-line workspace-skeleton-title workspace-skeleton-title-${titlePatterns[item % titlePatterns.length]} skeleton-shimmer`}
                                />
                                <div className="workspace-skeleton-line workspace-skeleton-subtitle skeleton-shimmer" />
                            </div>

                            <div className="workspace-skeleton-meta-stack">
                                {metaPatterns[item % metaPatterns.length].map((metaIdx) => (
                                    <div key={metaIdx} className="workspace-skeleton-meta-row">
                                        <div className="workspace-skeleton-meta-icon skeleton-shimmer" />
                                        <div
                                            className={`workspace-skeleton-line workspace-skeleton-meta workspace-skeleton-meta-variant-${metaIdx} skeleton-shimmer`}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="workspace-skeleton-footer">
                                <div className="workspace-skeleton-footer-left">
                                    {footerCommentPatterns[item % footerCommentPatterns.length] && (
                                        <div className="workspace-skeleton-comment-pill skeleton-shimmer" />
                                    )}
                                    <div className="workspace-skeleton-time skeleton-shimmer" />
                                </div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

const DashboardVerdictia = () => {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const { t: tWorkspace, i18n } = useTranslation("workspace");
    const dispatch = useDispatch();
    const contractsSectionRef = useRef(null);
    const contractsDisplayRef = useRef(null);
    const paginationRef = useRef(null);
    const pageSizeRef = useRef(12);
    const recalcFrameRef = useRef(null);

    const analysesList = useSelector((state) => state.review.summaryList) || [];
    const isLoading = useSelector((state) => state.review.isLoading);
    const first_login = useSelector((state) => state.users.first_login);
    const requires_policy_acceptance = useSelector((state) => state.users.requires_policy_acceptance);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const currentUser = useSelector((state) => (
        state.users?.user?.id ? state.users.user : state.users?.currentUser || {}
    ));
    const currentRole = String(currentUser?.role || "").toUpperCase();
    const isSuperAdmin = currentRole === "SUPER_ADMIN";
    const canManageBilling = ["SUPER_ADMIN", "ADMIN_CLIENT"].includes(currentRole);
    const billingReturnPath = currentRole === "SUPER_ADMIN"
        ? "/admin/commercial?tab=subscriptions"
        : "/workspace";

    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState("grid");
    const [sortBy, setSortBy] = useState("recent");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [deletingIds, setDeletingIds] = useState(() => new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ completed: 0, total: 0 });

    useEffect(() => {
        pageSizeRef.current = pageSize;
    }, [pageSize]);

    useEffect(() => {
        const nextPageSize = viewMode === "grid" ? 12 : 10;
        pageSizeRef.current = nextPageSize;
        setPageSize(nextPageSize);
    }, [viewMode]);

    const [filters, setFilters] = useState({
        type_analyse: null,
        document_type: null,
        language: null,
        jurisdiction: null,
        industry: null,
    });

    useEffect(() => {
        dispatch(RequestFetchAnalysesSummary());
    }, [dispatch]);

    useEffect(() => {
        if (commercialControl.usageSnapshot || commercialControl.isLoadingUsage) return;
        dispatch(fetchCommercialUsage()).catch(() => {});
    }, [commercialControl.isLoadingUsage, commercialControl.usageSnapshot, dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, filters, sortBy]);

    useEffect(() => {
        const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
        moment.locale(lang === "fr" ? "fr" : "en");
    }, [i18n.language]);

    const handleDelete = async (id) => {
        if (deletingIds.has(id)) return;

        setDeletingIds((current) => {
            const next = new Set(current);
            next.add(id);
            return next;
        });

        try {
            await dispatch(RequestDeleteAnalyse(id));
        } finally {
            setDeletingIds((current) => {
                const next = new Set(current);
                next.delete(id);
                return next;
            });
        }
    };

    const toggleSelection = useCallback((id) => {
        if (isBulkDeleting) return;
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, [isBulkDeleting]);

    const leaveSelectionMode = useCallback(() => {
        if (isBulkDeleting) return;
        setIsSelectionMode(false);
        setSelectedIds(new Set());
        setIsBulkDeleteModalOpen(false);
    }, [isBulkDeleting]);

    // ── derived facets (filter options w/ counts) ───────────────────────────
    const facets = useMemo(() => {
        const acc = {
            type_analyse: new Map(),
            document_type: new Map(),
            language: new Map(),
            jurisdiction: new Map(),
            industry: new Map(),
        };
        analysesList.forEach((a) => {
            if (a?.type_analyse) acc.type_analyse.set(a.type_analyse, (acc.type_analyse.get(a.type_analyse) || 0) + 1);
            if (a?.document_type) acc.document_type.set(a.document_type, (acc.document_type.get(a.document_type) || 0) + 1);
            if (a?.language) acc.language.set(a.language, (acc.language.get(a.language) || 0) + 1);
            normalizeArray(a?.jurisdiction).forEach((j) => acc.jurisdiction.set(j, (acc.jurisdiction.get(j) || 0) + 1));
            normalizeArray(a?.industry).forEach((j) => acc.industry.set(j, (acc.industry.get(j) || 0) + 1));
        });
        const toSorted = (m) =>
            Array.from(m.entries())
                .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
                .map(([value, count]) => ({ value, count }));
        return {
            type_analyse: toSorted(acc.type_analyse),
            document_type: toSorted(acc.document_type),
            language: toSorted(acc.language),
            jurisdiction: toSorted(acc.jurisdiction),
            industry: toSorted(acc.industry),
        };
    }, [analysesList]);

    // ── filtering + sorting ─────────────────────────────────────────────────
    const filteredData = useMemo(() => {
        const s = (search || "").trim().toLowerCase();
        const out = analysesList.filter((a) => {
            const title = (a?.title || "").toLowerCase();
            const mainTitle = (a?.main_document_title || "").toLowerCase();
            const matchSearch = !s || mainTitle.includes(s) || title.includes(s);
            const matchType = !filters.type_analyse || a.type_analyse === filters.type_analyse;
            const matchDoc = !filters.document_type || a.document_type === filters.document_type;
            const matchLang = !filters.language || a.language === filters.language;
            const matchJur = !filters.jurisdiction || normalizeArray(a.jurisdiction).includes(filters.jurisdiction);
            const matchInd = !filters.industry || normalizeArray(a.industry).includes(filters.industry);
            return matchSearch && matchType && matchDoc && matchLang && matchJur && matchInd;
        });

        const sorters = {
            recent: (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at),
            oldest: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            title_asc: (a, b) => (a.main_document_title || "").localeCompare(b.main_document_title || ""),
        };
        return [...out].sort(sorters[sortBy] || sorters.recent);
    }, [analysesList, search, filters, sortBy]);

    const filteredIds = useMemo(() => filteredData.map((analysis) => analysis.id), [filteredData]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

    const toggleAllFiltered = useCallback(() => {
        if (isBulkDeleting) return;
        setSelectedIds((current) => {
            const next = new Set(current);
            const shouldSelect = !filteredIds.every((id) => next.has(id));
            filteredIds.forEach((id) => {
                if (shouldSelect) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    }, [filteredIds, isBulkDeleting]);

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0 || isBulkDeleting) return;

        setIsBulkDeleting(true);
        setBulkDeleteProgress({ completed: 0, total: ids.length });
        setDeletingIds(new Set(ids));

        const failedIds = [];
        for (let index = 0; index < ids.length; index += 1) {
            const id = ids[index];
            const result = await dispatch(RequestDeleteAnalyse(id, { notify: false }));
            if (!result?.success) failedIds.push(id);
            setBulkDeleteProgress({ completed: index + 1, total: ids.length });
            setDeletingIds((current) => {
                const next = new Set(current);
                next.delete(id);
                return next;
            });
        }

        const deletedCount = ids.length - failedIds.length;
        if (deletedCount > 0) {
            message.success(tWorkspace("dashboard.bulk_delete.success", {
                count: deletedCount,
                defaultValue: `${deletedCount} analyse(s) supprimée(s).`,
            }));
        }
        if (failedIds.length > 0) {
            message.error(tWorkspace("dashboard.bulk_delete.failure", {
                count: failedIds.length,
                defaultValue: `${failedIds.length} suppression(s) ont échoué.`,
            }));
        }

        setSelectedIds(new Set(failedIds));
        setIsBulkDeleting(false);
        setIsBulkDeleteModalOpen(false);
        setDeletingIds(new Set());
        if (failedIds.length === 0) setIsSelectionMode(false);
    };


    const gridColumnCount = useMemo(
        () => (screens.xxl ? 4 : screens.xl ? 4 : screens.lg ? 3 : screens.sm ? 2 : 1),
        [screens.xxl, screens.xl, screens.lg, screens.sm]
    );

    const recalculatePageSize = useCallback(() => {
        const section = contractsSectionRef.current;
        const display = contractsDisplayRef.current;
        if (!section || !display || filteredData.length === 0) return;

        const sectionHeight = section.getBoundingClientRect().height;
        const paginationHeight = paginationRef.current?.getBoundingClientRect().height || 50;
        const footerGap = 28; // marge de sécurité pour éviter que la pagination soit rognée
        const availableHeight = Math.max(0, sectionHeight - paginationHeight - footerGap);
        const itemSelector = viewMode === "grid" ? ".contract-card" : ".contract-list-card";
        const renderedItems = Array.from(display.querySelectorAll(itemSelector));
        const fallbackItemHeight = viewMode === "grid" ? 250 : 64;
        const measuredItemHeight = renderedItems.reduce(
            (m, item) => Math.max(m, Math.ceil(item.getBoundingClientRect().height)),
            0
        );
        const itemHeight = measuredItemHeight || fallbackItemHeight;
        const itemGap = viewMode === "grid" ? 16 : 8;
        const rows = Math.max(1, Math.floor((availableHeight + itemGap) / (itemHeight + itemGap)));
        const columns = viewMode === "grid" ? gridColumnCount : 1;
        const minimumPageSize = viewMode === "grid" ? 12 : 10;
        const nextPageSize = Math.max(minimumPageSize, columns, rows * columns);
        if (pageSizeRef.current !== nextPageSize) {
            pageSizeRef.current = nextPageSize;
            setPageSize(nextPageSize);
        }
    }, [filteredData.length, gridColumnCount, viewMode]);

    const schedulePageSizeRecalculation = useCallback(() => {
        if (recalcFrameRef.current) {
            window.cancelAnimationFrame(recalcFrameRef.current);
        }
        recalcFrameRef.current = window.requestAnimationFrame(() => {
            recalcFrameRef.current = null;
            recalculatePageSize();
        });
    }, [recalculatePageSize]);

    useLayoutEffect(() => {
        schedulePageSizeRecalculation();
        const section = contractsSectionRef.current;
        if (!section) return undefined;
        const ro = new ResizeObserver(schedulePageSizeRecalculation);
        ro.observe(section);
        window.addEventListener("resize", schedulePageSizeRecalculation);
        return () => {
            if (recalcFrameRef.current) {
                window.cancelAnimationFrame(recalcFrameRef.current);
                recalcFrameRef.current = null;
            }
            ro.disconnect();
            window.removeEventListener("resize", schedulePageSizeRecalculation);
        };
    }, [schedulePageSizeRecalculation]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize));
        setCurrentPage((prev) => Math.min(prev, maxPage));
    }, [filteredData.length, pageSize]);

    const total = filteredData.length;
    const paged = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    // Changes on every pagination/filter/search action → fed as `key` to the
    // grid / list container so React remounts all cards and CSS animation runs.
    const displayKey = `${currentPage}|${sortBy}|${search}|${JSON.stringify(filters)}`;

    // Returns the inline animation style for index-th card.
    const cardAnim = (index, isGrid) => ({
        animation: `card-enter ${isGrid ? 360 : 300}ms cubic-bezier(0.22,1,0.36,1) ${Math.min(index, 10) * (isGrid ? 46 : 30)}ms both`,
    });

    const openContract = (id) => window.open(`/stage-review/${id}`, "_blank");

    const activeFilterCount = Object.values(filters).filter(Boolean).length;
    const showSkeleton = isLoading && analysesList.length === 0;
    const clearFilter = (key) => setFilters((f) => ({ ...f, [key]: null }));
    const clearAllFilters = () =>
        setFilters({ type_analyse: null, document_type: null, language: null, jurisdiction: null, industry: null });
    const unavailableLabel = tWorkspace("dashboard.meta.not_available", { defaultValue: "Non disponible" });
    const reduxUsageByType = commercialControl.usageByType || {};
    const usageByType = Object.keys(reduxUsageByType).length
        ? reduxUsageByType
        : getUsageByTypeFromSnapshot(commercialControl.usageSnapshot);
    const hasUsageSnapshot = !!commercialControl.usageSnapshot || Object.keys(usageByType).length > 0;
    const canCreateAnalysis = hasUsageSnapshot && canStartCompleteAnalysisFromUsage(usageByType);
    const createAnalysisLocked = hasUsageSnapshot && !canCreateAnalysis;

    const openCreateAnalysis = () => window.open("/stage-review/create", "_blank");

    const showCreateAnalysisLocked = () => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision("complete_analysis", {
            code: "complete_analysis_quota_reached",
            titleFr: "Analyse verrouillée",
            bodyFr: "Votre workspace a atteint le quota d'analyses incluses dans le plan actif. Consultez les options pour lancer une nouvelle analyse.",
            titleEn: "Analysis locked",
            bodyEn: "Your workspace has reached the analyses included in the active plan. View options to start a new analysis.",
            metadata_json: {
                source: "workspace_create_analysis",
                usage_type: "complete_analysis",
            },
        })));
    };

    const handleCreateAnalysisClick = async () => {
        if (commercialControl.isLoadingUsage) return;

        try {
            const usageSnapshot = await dispatch(fetchCommercialUsage());
            if (canStartCompleteAnalysisFromUsage(getUsageByTypeFromSnapshot(usageSnapshot))) {
                openCreateAnalysis();
                return;
            }
        } catch {
            // The backend launch gate remains authoritative; keep the CTA closed on stale data.
        }

        showCreateAnalysisLocked();
    };

    // ── Filter dropdown menu builder ────────────────────────────────────────
    const buildFilterMenu = (key, options, allLabel) => ({
        items: [
            {
                key: "__all__",
                label: (
                    <span className="hv-filter-item hv-filter-item-all">
                        {filters[key] === null ? <CheckOutlined /> : <span className="hv-check-spacer" />}
                        {allLabel}
                    </span>
                ),
                onClick: () => clearFilter(key),
            },
            { type: "divider" },
            ...options.map((opt) => ({
                key: opt.value,
                label: (
                    <span className="hv-filter-item">
                        {filters[key] === opt.value ? <CheckOutlined /> : <span className="hv-check-spacer" />}
                        <span className="hv-filter-item-label">{opt.value}</span>
                        <span className="hv-filter-item-count">{opt.count}</span>
                    </span>
                ),
                onClick: () => setFilters((f) => ({ ...f, [key]: opt.value })),
            })),
        ],
    });

    // ── ContractCard (grid) ─────────────────────────────────────────────────
    const ContractCard = ({ contract }) => {
        const industries = normalizeArray(contract.industry);
        const jurisdictions = normalizeArray(contract.jurisdiction);
        const showSubtitle = contract.title && contract.title !== contract.main_document_title;
        const isFresh = contract.created_at && moment().diff(moment(contract.created_at), "hours") < 24;
        const partyRoleText = contract.party_role || unavailableLabel;
        const jurisdictionText = jurisdictions.length > 0 ? jurisdictions.join(" · ") : unavailableLabel;
        const industryText = industries.length > 0 ? industries.join(" · ") : unavailableLabel;
        const isDeleting = deletingIds.has(contract.id);
        const isSelected = selectedIds.has(contract.id);

        return (
            <Card
                className={`contract-card${isDeleting ? " is-deleting" : ""}${isSelectionMode ? " is-selectable" : ""}${isSelected ? " is-selected" : ""}`}
                hoverable={!isDeleting}
                role="button"
                tabIndex={isDeleting ? -1 : 0}
                aria-busy={isDeleting}
                onClick={() => {
                    if (isDeleting) return;
                    if (isSelectionMode) toggleSelection(contract.id);
                    else openContract(contract.id);
                }}
                onKeyDown={(e) => {
                    if (isDeleting) return;
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (isSelectionMode) toggleSelection(contract.id);
                        else openContract(contract.id);
                    }
                }}
                aria-pressed={isSelectionMode ? isSelected : undefined}
            >
                {/* Accent bar latéral — signature Verdictia */}
                <span className="cc-accent-bar" aria-hidden="true" />
                {isSelectionMode && (
                    <Checkbox
                        className="contract-selection-checkbox"
                        checked={isSelected}
                        disabled={isBulkDeleting}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSelection(contract.id)}
                        aria-label={tWorkspace("dashboard.selection.toggle", {
                            title: contract.main_document_title,
                            defaultValue: `Sélectionner ${contract.main_document_title || "cette analyse"}`,
                        })}
                    />
                )}
                {isDeleting && (
                    <div className="contract-delete-overlay" aria-hidden="true">
                        <span className="contract-delete-spinner" />
                    </div>
                )}

                <div className="cc-inner">
                    {/* Top tag row */}
                    <div className="cc-tag-row">
                        <div className="cc-tag-row-left">
                            <span className="cc-doc-sigil" aria-hidden="true">
                                <FileTextOutlined />
                            </span>
                            {contract.type_analyse && (
                                <span className="cc-pill cc-pill-type" title={contract.type_analyse}>
                                    <span className="cc-pill-dot" />
                                    {contract.type_analyse}
                                </span>
                            )}
                            {contract.document_type && (
                                <span className="cc-pill cc-pill-doc" title={contract.document_type}>
                                    {contract.document_type}
                                </span>
                            )}
                        </div>
                        <div className="cc-tag-row-right">
                            {isFresh && (
                                <Tooltip title={tWorkspace("dashboard.fresh", { defaultValue: "Ajouté récemment" })}>
                                    <span className="cc-fresh-dot" aria-label="new" />
                                </Tooltip>
                            )}
                            {contract.language && <span className="cc-lang">{String(contract.language).toUpperCase()}</span>}
                        </div>
                    </div>

                    {/* Titles */}
                    <div className="cc-titles">
                        <h3 className="cc-main-title" title={contract.main_document_title}>
                            {contract.main_document_title || tWorkspace("dashboard.untitled", { defaultValue: "Sans titre" })}
                        </h3>
                        {showSubtitle && (
                            <p className="cc-sub-title" title={contract.title}>
                                {contract.title}
                            </p>
                        )}
                        {!showSubtitle && <p className="cc-sub-title cc-sub-title-placeholder">{unavailableLabel}</p>}
                    </div>

                    {/* Meta inline */}
                    <ul className="cc-meta-inline">
                        <li className={`cc-meta-chunk${contract.party_role ? "" : " is-muted"}`} title={partyRoleText}>
                            <UserOutlined />
                            <span>{partyRoleText}</span>
                        </li>
                        <li className={`cc-meta-chunk${jurisdictions.length > 0 ? "" : " is-muted"}`} title={jurisdictionText}>
                            <GlobalOutlined />
                            <span>{jurisdictionText}</span>
                        </li>
                        <li className={`cc-meta-chunk${industries.length > 0 ? "" : " is-muted"}`} title={industryText}>
                            <ApartmentOutlined />
                            <span>{industryText}</span>
                        </li>
                    </ul>

                    {/* Footer */}
                    <div className="cc-footer">
                        <div className="cc-footer-left">
                            {contract.comments_count > 0 && (
                                <Tooltip title={tWorkspace("dashboard.comments.count", { count: contract.comments_count, defaultValue: `${contract.comments_count} commentaires` })}>
                                    <span className="cc-comments">
                                        <MessageOutlined />
                                        {contract.comments_count}
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title={moment(contract.updated_at).format("DD MMM YYYY · HH:mm")}>
                                <span className="cc-updated">
                                    <ClockCircleOutlined />
                                    {moment(contract.updated_at).fromNow()}
                                </span>
                            </Tooltip>
                        </div>

                        <div className="cc-footer-right" onClick={(e) => e.stopPropagation()}>
                            {isSuperAdmin && !isSelectionMode && (
                                <Popconfirm
                                    title={tWorkspace("dashboard.delete.title")}
                                    description={tWorkspace("dashboard.delete.desc")}
                                    okText={tWorkspace("actions.delete")}
                                    cancelText={tWorkspace("actions.cancel")}
                                    okButtonProps={{ danger: true, loading: isDeleting, disabled: isDeleting }}
                                    cancelButtonProps={{ disabled: isDeleting }}
                                    disabled={isDeleting}
                                    onConfirm={(e) => {
                                        e?.stopPropagation();
                                        handleDelete(contract.id);
                                    }}
                                    onCancel={(e) => e?.stopPropagation()}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        className="footer-delete-btn"
                                        loading={isDeleting}
                                        disabled={isDeleting}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={tWorkspace("actions.delete")}
                                    />
                                </Popconfirm>
                            )}
                            {!isSelectionMode && (
                                <span className="cc-open-arrow" aria-hidden="true">
                                    <ArrowRightOutlined />
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    // ── CompactCard (list) ──────────────────────────────────────────────────
    const CompactCard = ({ contract }) => {
        const industries = normalizeArray(contract.industry);
        const jurisdictions = normalizeArray(contract.jurisdiction);
        const isFresh = contract.created_at && moment().diff(moment(contract.created_at), "hours") < 24;
        const partyRoleText = contract.party_role || unavailableLabel;
        const jurisdictionText = jurisdictions.length > 0 ? jurisdictions.join(" · ") : unavailableLabel;
        const industryText = industries.length > 0 ? industries.join(" · ") : unavailableLabel;
        const isDeleting = deletingIds.has(contract.id);
        const isSelected = selectedIds.has(contract.id);

        return (
            <Card
                className={`contract-list-card${isDeleting ? " is-deleting" : ""}${isSelectionMode ? " is-selectable" : ""}${isSelected ? " is-selected" : ""}`}
                hoverable={!isDeleting}
                aria-busy={isDeleting}
                onClick={() => {
                    if (isDeleting) return;
                    if (isSelectionMode) toggleSelection(contract.id);
                    else openContract(contract.id);
                }}
                aria-pressed={isSelectionMode ? isSelected : undefined}
            >
                {isDeleting && (
                    <div className="contract-delete-overlay" aria-hidden="true">
                        <span className="contract-delete-spinner" />
                    </div>
                )}
                <div className="list-card-content">
                    {isSelectionMode && (
                        <Checkbox
                            className="contract-selection-checkbox contract-selection-checkbox-list"
                            checked={isSelected}
                            disabled={isBulkDeleting}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleSelection(contract.id)}
                            aria-label={tWorkspace("dashboard.selection.toggle", {
                                title: contract.main_document_title,
                                defaultValue: `Sélectionner ${contract.main_document_title || "cette analyse"}`,
                            })}
                        />
                    )}
                    <div className="lc-doc-sigil" aria-hidden="true">
                        <FileTextOutlined />
                    </div>

                    <div className="list-card-info">
                        <div className="lc-top-row">
                            <h4 className="list-card-title" title={contract.main_document_title}>
                                {contract.main_document_title || tWorkspace("dashboard.untitled", { defaultValue: "Sans titre" })}
                            </h4>
                            {contract.type_analyse && (
                                <span className="cc-pill cc-pill-type cc-pill-sm">
                                    <span className="cc-pill-dot" />
                                    {contract.type_analyse}
                                </span>
                            )}
                            {contract.document_type && <span className="cc-pill cc-pill-doc cc-pill-sm">{contract.document_type}</span>}
                            {isFresh && <span className="cc-fresh-dot" aria-label="new" />}
                        </div>

                        <div className="list-card-meta">
                            <span className={`lc-meta-chunk${contract.party_role ? "" : " is-muted"}`}>
                                <UserOutlined />
                                {partyRoleText}
                            </span>
                            <span className={`lc-meta-chunk${jurisdictions.length > 0 ? "" : " is-muted"}`}>
                                <GlobalOutlined />
                                {jurisdictionText}
                            </span>
                            <span className={`lc-meta-chunk${industries.length > 0 ? "" : " is-muted"}`}>
                                <ApartmentOutlined />
                                {industryText}
                            </span>
                            {contract.language && (
                                <span className="lc-meta-chunk lc-meta-mono">
                                    <TagOutlined />
                                    {String(contract.language).toUpperCase()}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="list-card-right">
                        {contract.comments_count > 0 && (
                            <span className="cc-comments">
                                <MessageOutlined />
                                {contract.comments_count}
                            </span>
                        )}
                        <Tooltip title={moment(contract.updated_at).format("DD MMM YYYY · HH:mm")}>
                            <span className="lc-meta-mono lc-date">
                                <CalendarOutlined />
                                {moment(contract.updated_at).format("DD MMM")}
                            </span>
                        </Tooltip>

                        {isSuperAdmin && !isSelectionMode && (
                            <Popconfirm
                                title={tWorkspace("dashboard.delete.title")}
                                description={tWorkspace("dashboard.delete.desc")}
                                okText={tWorkspace("actions.delete")}
                                cancelText={tWorkspace("actions.cancel")}
                                okButtonProps={{ danger: true, loading: isDeleting, disabled: isDeleting }}
                                cancelButtonProps={{ disabled: isDeleting }}
                                disabled={isDeleting}
                                onConfirm={(e) => {
                                    e?.stopPropagation();
                                    handleDelete(contract.id);
                                }}
                                onCancel={(e) => e?.stopPropagation()}
                            >
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    className="footer-delete-btn"
                                    loading={isDeleting}
                                    disabled={isDeleting}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={tWorkspace("actions.delete")}
                                />
                            </Popconfirm>
                        )}

                        {!isSelectionMode && (
                            <span className="cc-open-arrow" aria-hidden="true">
                                <ArrowRightOutlined />
                            </span>
                        )}
                    </div>
                </div>
            </Card>
        );
    };

    const startItem = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, total);
    const totalPages = Math.ceil(total / pageSize);

    const currentSortOpt = SORT_OPTIONS.find((o) => o.key === sortBy) || SORT_OPTIONS[0];
    const sortLabel = tWorkspace(currentSortOpt.labelKey, { defaultValue: currentSortOpt.defaultLabel });

    const filterChips = [
        { key: "type_analyse", label: tWorkspace("dashboard.filters.type_analyse", { defaultValue: "Analyse" }) },
        { key: "document_type", label: tWorkspace("dashboard.filters.document_type", { defaultValue: "Document" }) },
        { key: "jurisdiction", label: tWorkspace("dashboard.filters.jurisdiction", { defaultValue: "Juridiction" }) },
        { key: "industry", label: tWorkspace("dashboard.filters.industry", { defaultValue: "Secteur" }) },
        { key: "language", label: tWorkspace("dashboard.filters.language", { defaultValue: "Langue" }) },
    ];

    return (
        <div className="dashboard-container">
            {/* ── Hero header (titre retiré : doublon avec layout) ──── */}
            <div className="hv-hero">
                <div className="hv-hero-text">
                    <p className="hv-hero-sub">
                        {tWorkspace("dashboard.hero.sub", {
                            defaultValue: "Vos documents contractuels, regroupés et triés par activité récente.",
                        })}
                    </p>
                </div>

                <Button
                    type="primary"
                    icon={createAnalysisLocked ? <LockOutlined /> : <PlusOutlined />}
                    size="large"
                    onClick={handleCreateAnalysisClick}
                    loading={commercialControl.isLoadingUsage && !hasUsageSnapshot}
                    className={`create-btn verdictia-primary-btn${createAnalysisLocked ? " create-btn-locked" : ""}`}
                >
                    {createAnalysisLocked
                        ? tWorkspace("actions.new_locked", { defaultValue: "Analyse verrouillée" })
                        : tWorkspace("actions.new")}
                </Button>
            </div>

            {/* ── Toolbar ─────────────────────────────────────────────── */}
            <div className="hv-toolbar">
                <Input
                    placeholder={tWorkspace("dashboard.search.placeholder")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                    size="large"
                    className="hv-search"
                />

                <div className="hv-toolbar-right">
                    {isSuperAdmin && !isSelectionMode && total > 0 && (
                        <Button
                            className="hv-tool-btn"
                            icon={<CheckSquareOutlined />}
                            onClick={() => setIsSelectionMode(true)}
                            aria-label={tWorkspace("dashboard.selection.start", { defaultValue: "Sélectionner" })}
                        >
                            {tWorkspace("dashboard.selection.start", { defaultValue: "Sélectionner" })}
                        </Button>
                    )}
                    <Dropdown
                        menu={{
                            items: SORT_OPTIONS.map((o) => ({
                                key: o.key,
                                label: (
                                    <span className="hv-filter-item">
                                        {sortBy === o.key ? <CheckOutlined /> : <span className="hv-check-spacer" />}
                                        <span className="hv-filter-item-label">
                                            {tWorkspace(o.labelKey, { defaultValue: o.defaultLabel })}
                                        </span>
                                    </span>
                                ),
                                onClick: () => setSortBy(o.key),
                            })),
                        }}
                        trigger={["click"]}
                        placement="bottomRight"
                    >
                        <Button className="hv-tool-btn" icon={<SortAscendingOutlined />}>
                            {sortLabel}
                        </Button>
                    </Dropdown>

                    {filterChips.map((fc) =>
                        facets[fc.key].length > 0 ? (
                            <Dropdown
                                key={fc.key}
                                menu={buildFilterMenu(
                                    fc.key,
                                    facets[fc.key],
                                    tWorkspace("dashboard.filters.all", { defaultValue: "Tous" })
                                )}
                                trigger={["click"]}
                                placement="bottomRight"
                            >
                                <Button
                                    className={`hv-tool-btn ${filters[fc.key] ? "hv-tool-btn-active" : ""}`}
                                    icon={<FilterOutlined />}
                                >
                                    {fc.label}
                                    {filters[fc.key] && <span className="hv-tool-btn-value">: {filters[fc.key]}</span>}
                                </Button>
                            </Dropdown>
                        ) : null
                    )}

                    <Space.Compact className="hv-view-toggle">
                        <Tooltip title={tWorkspace("dashboard.view.grid")}>
                            <Button
                                icon={<AppstoreOutlined />}
                                onClick={() => setViewMode("grid")}
                                type={viewMode === "grid" ? "primary" : "default"}
                            />
                        </Tooltip>
                        <Tooltip title={tWorkspace("dashboard.view.list")}>
                            <Button
                                icon={<UnorderedListOutlined />}
                                onClick={() => setViewMode("list")}
                                type={viewMode === "list" ? "primary" : "default"}
                            />
                        </Tooltip>
                    </Space.Compact>
                </div>
            </div>

            {isSelectionMode && (
                <div className="workspace-selection-bar" role="region" aria-label={tWorkspace("dashboard.selection.label", { defaultValue: "Sélection multiple" })}>
                    <div className="workspace-selection-status">
                        <Checkbox
                            checked={allFilteredSelected}
                            indeterminate={selectedIds.size > 0 && !allFilteredSelected}
                            disabled={isBulkDeleting || filteredIds.length === 0}
                            onChange={toggleAllFiltered}
                        >
                            {allFilteredSelected
                                ? tWorkspace("dashboard.selection.clear_all", { defaultValue: "Tout désélectionner" })
                                : tWorkspace("dashboard.selection.select_all", { count: filteredIds.length, defaultValue: `Tout sélectionner (${filteredIds.length})` })}
                        </Checkbox>
                        <span className="workspace-selection-count">
                            {tWorkspace("dashboard.selection.count", { count: selectedIds.size, defaultValue: `${selectedIds.size} sélectionnée(s)` })}
                        </span>
                    </div>
                    <div className="workspace-selection-actions">
                        <Button disabled={isBulkDeleting} onClick={leaveSelectionMode}>
                            {tWorkspace("actions.cancel")}
                        </Button>
                        <Button
                            danger
                            type="primary"
                            icon={<DeleteOutlined />}
                            disabled={selectedIds.size === 0 || isBulkDeleting}
                            loading={isBulkDeleting}
                            onClick={() => setIsBulkDeleteModalOpen(true)}
                            aria-label={tWorkspace("dashboard.selection.delete", { count: selectedIds.size, defaultValue: `Supprimer (${selectedIds.size})` })}
                        >
                            {tWorkspace("dashboard.selection.delete", { count: selectedIds.size, defaultValue: `Supprimer (${selectedIds.size})` })}
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Active filters / results count ──────────────────────── */}
            <div className="hv-active-filters">
                <span className="hv-active-count">
                    {tWorkspace("dashboard.results.count", { count: total })}
                </span>
                {Object.entries(filters)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                        <button key={k} className="hv-chip" onClick={() => clearFilter(k)}>
                            <span className="hv-chip-key">
                                {filterChips.find((c) => c.key === k)?.label || k}
                            </span>
                            <span className="hv-chip-val">{v}</span>
                            <CloseOutlined />
                        </button>
                    ))}
                {activeFilterCount > 0 && (
                    <button className="hv-chip-clear" onClick={clearAllFilters}>
                        {tWorkspace("dashboard.filters.clear_all", { defaultValue: "Tout effacer" })}
                    </button>
                )}
            </div>

            {/* ── Display ─────────────────────────────────────────────── */}
            <div className="contracts-section" ref={contractsSectionRef}>
                {showSkeleton ? (
                    <WorkspaceSkeleton
                        viewMode={viewMode}
                        count={viewMode === "grid" ? pageSize : Math.min(pageSize, 8)}
                        tWorkspace={tWorkspace}
                    />
                ) : total === 0 ? (
                    <Empty
                        description={tWorkspace("dashboard.empty")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className="empty-state"
                    />
                ) : (
                    <>
                        <div className="contracts-display" ref={contractsDisplayRef}>
                            {viewMode === "grid" ? (
                                <Row gutter={[16, 16]} key={displayKey}>
                                    {paged.map((contract, index) => (
                                        <Col xs={24} sm={12} lg={8} xl={6} xxl={6} key={contract.id}
                                            style={{ ...cardAnim(index, true), height: '100%' }}>
                                            <ContractCard contract={contract} />
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <div className="contracts-list" key={displayKey}>
                                    {paged.map((contract, index) => (
                                        <div key={contract.id} style={cardAnim(index, false)}>
                                            <CompactCard contract={contract} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {total > pageSize && (
                            <div className="pagination-wrapper" ref={paginationRef}>
                                <div className="pagination-compact">
                                    <div className="pagination-info">
                                        {tWorkspace("dashboard.pagination.showing", {
                                            start: startItem,
                                            end: endItem,
                                            total,
                                        })}
                                    </div>
                                    <Space className="pagination-controls" size={8}>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentPage((p) => p - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            {tWorkspace("actions.previous")}
                                        </Button>
                                        <span className="pagination-pages">
                                            {tWorkspace("dashboard.pagination.page_of", {
                                                page: currentPage,
                                                totalPages,
                                            })}
                                        </span>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentPage((p) => p + 1)}
                                            disabled={currentPage >= totalPages}
                                        >
                                            {tWorkspace("actions.next")}
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <PasswordChangeModal visible={!requires_policy_acceptance && first_login} />
            <CommercialGateModal
                open={!!commercialControl.gateModalOpen}
                decision={commercialControl.lastDecision}
                currentPlan={commercialControl.plan || commercialControl.usageSnapshot?.plan}
                usageSnapshot={commercialControl.usageSnapshot}
                canManageBilling={canManageBilling}
                billingReturnPath={billingReturnPath}
                onClose={() => dispatch(closeCommercialGateModal())}
            />
            <Modal
                open={isBulkDeleteModalOpen}
                title={tWorkspace("dashboard.bulk_delete.title", {
                    count: selectedIds.size,
                    defaultValue: `Supprimer ${selectedIds.size} analyse(s) ?`,
                })}
                okText={isBulkDeleting
                    ? tWorkspace("dashboard.bulk_delete.progress", {
                        completed: bulkDeleteProgress.completed,
                        total: bulkDeleteProgress.total,
                        defaultValue: `${bulkDeleteProgress.completed}/${bulkDeleteProgress.total}`,
                    })
                    : tWorkspace("dashboard.bulk_delete.confirm", { defaultValue: "Supprimer définitivement" })}
                cancelText={tWorkspace("actions.cancel")}
                okButtonProps={{ danger: true, loading: isBulkDeleting, disabled: isBulkDeleting }}
                cancelButtonProps={{ disabled: isBulkDeleting }}
                closable={!isBulkDeleting}
                maskClosable={!isBulkDeleting}
                keyboard={!isBulkDeleting}
                onOk={handleBulkDelete}
                onCancel={() => !isBulkDeleting && setIsBulkDeleteModalOpen(false)}
                className="workspace-bulk-delete-modal"
            >
                <p className="workspace-bulk-delete-warning">
                    {tWorkspace("dashboard.bulk_delete.description", {
                        count: selectedIds.size,
                        defaultValue: "Les analyses sélectionnées, toutes leurs versions et leurs commentaires associés seront supprimés. Cette action est irréversible.",
                    })}
                </p>
                {isBulkDeleting && (
                    <div className="workspace-bulk-delete-progress" aria-live="polite">
                        <span>{tWorkspace("dashboard.bulk_delete.deleting", { defaultValue: "Suppression en cours" })}</span>
                        <strong>{bulkDeleteProgress.completed} / {bulkDeleteProgress.total}</strong>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DashboardVerdictia;

